import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Mail, MessageCircle, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toCsv, downloadCsv } from "@/lib/csv";

export const Route = createFileRoute("/_authenticated/messages")({
  head: () => ({
    meta: [
      { title: "Enquiries | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Private inbox of parent enquiries sent through the Smart Word Labs website.",
      },
      { property: "og:title", content: "Enquiries | Smart Word Labs" },
      { property: "og:description", content: "Private inbox of parent enquiries." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MessagesPage,
});

type ContactMessage = {
  id: string;
  name: string;
  email: string;
  whatsapp: string | null;
  child_age: string | null;
  subject: string | null;
  message: string;
  status: string;
  created_at: string;
};

function MessagesPage() {
  const { t } = useTranslation("portal");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["contact-messages"],
    queryFn: async (): Promise<ContactMessage[]> => {
      const { data, error } = await supabase
        .from("contact_messages")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ContactMessage[];
    },
  });

  async function markHandled(id: string, status: string) {
    const next = status === "handled" ? "new" : "handled";
    const { error } = await supabase
      .from("contact_messages")
      .update({ status: next })
      .eq("id", id);
    if (error) {
      toast.error(t("messagesPage.toasts.updateFailed"));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ["contact-messages"] });
  }

  const messages = data ?? [];

  function exportCsv() {
    if (messages.length === 0) {
      toast.error(t("messagesPage.toasts.nothingToExport"));
      return;
    }
    const csv = toCsv(
      [
        t("messagesPage.csv.headers.received"),
        t("messagesPage.csv.headers.name"),
        t("messagesPage.csv.headers.email"),
        t("messagesPage.csv.headers.whatsapp"),
        t("messagesPage.csv.headers.childAge"),
        t("messagesPage.csv.headers.subject"),
        t("messagesPage.csv.headers.message"),
        t("messagesPage.csv.headers.status"),
      ],
      messages.map((m) => [
        new Date(m.created_at).toISOString(),
        m.name,
        m.email,
        m.whatsapp ?? "",
        m.child_age ?? "",
        m.subject ?? "",
        m.message,
        m.status,
      ]),
    );
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`${t("messagesPage.csv.filename", { date: stamp })}.csv`, csv);
    toast.success(t("messagesPage.toasts.exported", { count: messages.length }));
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-accent"
      >
        <ArrowLeft className="size-4" /> {t("messagesPage.backToPortal")}
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground">
            {t("messagesPage.title")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("messagesPage.description")}</p>
        </div>
        <Button
          variant="outline"
          onClick={exportCsv}
          disabled={isLoading || messages.length === 0}
          className="gap-2"
        >
          <Download className="size-4" /> {t("messagesPage.exportCsv")}
        </Button>
      </div>

      {isLoading ? (
        <div className="mt-10 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t("messagesPage.loading")}
        </div>
      ) : messages.length === 0 ? (
        <p className="mt-10 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          {t("messagesPage.empty")}
        </p>
      ) : (
        <ul className="mt-8 space-y-4">
          {messages.map((m) => (
            <li key={m.id} className="rounded-xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-foreground">
                    {m.name}
                    {m.child_age ? (
                      <span className="ms-2 text-xs font-normal text-muted-foreground">
                        {t("messagesPage.childAge", { age: m.child_age })}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={m.status === "handled" ? "secondary" : "default"}>
                    {m.status === "handled"
                      ? t("messagesPage.status.handled")
                      : t("messagesPage.status.new")}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => markHandled(m.id, m.status)}>
                    {m.status === "handled"
                      ? t("messagesPage.actions.reopen")
                      : t("messagesPage.actions.markHandled")}
                  </Button>
                </div>
              </div>

              {m.subject ? (
                <p className="mt-3 text-sm font-medium text-foreground">{m.subject}</p>
              ) : null}
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{m.message}</p>

              <div className="mt-4 flex flex-wrap gap-4 border-t border-border pt-3 text-sm">
                <a
                  href={`mailto:${m.email}`}
                  className="inline-flex items-center gap-2 text-primary hover:text-accent"
                >
                  <Mail className="size-4" /> {m.email}
                </a>
                {m.whatsapp ? (
                  <a
                    href={`https://wa.me/${m.whatsapp.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:text-accent"
                  >
                    <MessageCircle className="size-4" /> {m.whatsapp}
                  </a>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
